/**
 * Math fingerprint probes: one try/catch per property so a single failure drops only that key.
 * Polyfills use alternate formulas to surface different rounding than native Math.*.
 */

const M = Math;

function polyLog1p(x) {
    return M.log(1 + x);
}

function polyExpm1(x) {
    return M.exp(x) - 1;
}

function polyAsinh(x) {
    return M.log(x + M.sqrt(x * x + 1));
}

function polyAcosh(x) {
    return M.log(x + M.sqrt(x * x - 1));
}

function polyAtanh(x) {
    return M.log((1 + x) / (1 - x)) / 2;
}

function polySinh(x) {
    return (M.exp(x) - M.exp(-x)) / 2;
}

function polyCosh(x) {
    return (M.exp(x) + M.exp(-x)) / 2;
}

function polyTanh(x) {
    const t = M.exp(2 * x);
    return (t - 1) / (t + 1);
}

function polyPow(x, y) {
    return M.exp(y * M.log(x));
}

function polyLog2(x) {
    return M.log(x) / M.LN2;
}

/**
 * @param {unknown} v
 * @returns {number|string}
 */
function serializeNumberLike(v) {
    if (typeof v === 'boolean') {
        return v ? 1 : 0;
    }
    if (typeof v !== 'number') {
        return /** @type {string} */ (String(v));
    }
    if (Number.isFinite(v)) {
        return v;
    }
    if (Number.isNaN(v)) {
        return 'NaN';
    }
    if (v === Infinity) {
        return 'Infinity';
    }
    if (v === -Infinity) {
        return '-Infinity';
    }
    return v;
}

/**
 * @param {Record<string, number|string>} out
 * @param {string} key
 * @param {() => unknown} fn
 */
function trySet(out, key, fn) {
    try {
        out[key] = serializeNumberLike(fn());
    } catch (_) {
        /* omit key */
    }
}

/**
 * @returns {Record<string, number|string>}
 */
export function getMathFingerprint() {
    /** @type {Record<string, number|string>} */
    const out = {};

    // --- original probes (same inputs as before) ---
    trySet(out, 'acos', () => M.acos(0.123124234234234242));
    trySet(out, 'acosh', () => M.acosh(1e308));
    trySet(out, 'asin', () => M.asin(0.123124234234234242));
    trySet(out, 'asinh', () => M.asinh(1));
    trySet(out, 'atanh', () => M.atanh(0.5));
    trySet(out, 'atan', () => M.atan(0.5));
    trySet(out, 'sin', () => M.sin(-1e300));
    trySet(out, 'sinh', () => M.sinh(1));
    trySet(out, 'cos', () => M.cos(10.000000000123));
    trySet(out, 'cosh', () => M.cosh(1));
    trySet(out, 'tan', () => M.tan(-1e300));
    trySet(out, 'tanh', () => M.tanh(1));
    trySet(out, 'exp', () => M.exp(1));
    trySet(out, 'expm1', () => M.expm1(1));
    trySet(out, 'log1p', () => M.log1p(10));

    // --- poly / alternate formulas (competitor-style *Pf) ---
    trySet(out, 'acoshPf', () => polyAcosh(1e154));
    trySet(out, 'asinhPf', () => polyAsinh(1));
    trySet(out, 'atanhPf', () => polyAtanh(0.5));
    trySet(out, 'sinhPf', () => polySinh(1));
    trySet(out, 'coshPf', () => polyCosh(1));
    trySet(out, 'tanhPf', () => polyTanh(1));
    trySet(out, 'expm1Pf', () => polyExpm1(1));
    trySet(out, 'log1pPf', () => polyLog1p(10));
    trySet(out, 'powPiNeg100', () => M.pow(M.PI, -100));

    // --- powers / roots ---
    trySet(out, 'pow2e53', () => M.pow(2, 53));
    trySet(out, 'sqrt0123', () => M.sqrt(0.123));
    trySet(out, 'cbrt100', () => M.cbrt(100));
    trySet(out, 'pow1eNeg300Sq', () => M.pow(1e-300, 2));
    trySet(out, 'pow1e308Sq', () => M.pow(1e308, 2));
    trySet(out, 'pow2em3Neg100', () => M.pow(2e-3, -100));
    trySet(out, 'polyPow2em3Neg100', () => polyPow(2e-3, -100));

    // --- trig ---
    trySet(out, 'sinPi', () => M.sin(M.PI));
    trySet(out, 'cos21Ln2', () => M.cos(21 * M.LN2));
    trySet(out, 'tanPi', () => M.tan(M.PI));
    trySet(out, 'acos0123', () => M.acos(0.123));
    trySet(out, 'asin0123', () => M.asin(0.123));
    trySet(out, 'atanOf2', () => M.atan(2));
    trySet(out, 'atanPi', () => M.atan(M.PI));
    trySet(out, 'atan2ZeroNegZero', () => M.atan2(0, -0));
    trySet(out, 'atan2Tiny', () => M.atan2(1e-310, 2));

    // --- hyperbolic (extra arguments) ---
    trySet(out, 'sinhPi', () => M.sinh(M.PI));
    trySet(out, 'coshPi', () => M.cosh(M.PI));
    trySet(out, 'tanh0123', () => M.tanh(0.123));
    trySet(out, 'acoshPi', () => M.acosh(M.PI));
    trySet(out, 'polyAtanh05', () => polyAtanh(0.5));

    // --- log / exp ---
    trySet(out, 'logPi', () => M.log(M.PI));
    trySet(out, 'log1pPi', () => M.log1p(M.PI));
    trySet(out, 'log1pNeg1', () => M.log1p(-1));
    trySet(out, 'log10Log10E', () => {
        if (typeof M.log10 !== 'function') {
            throw new Error('no log10');
        }
        return M.log10(M.LOG10E);
    });
    trySet(out, 'polyLog1pPi', () => polyLog1p(M.PI));
    trySet(out, 'polyLog210', () => polyLog2(10));
    trySet(out, 'log210', () => {
        if (typeof M.log2 !== 'function') {
            throw new Error('no log2');
        }
        return M.log2(10);
    });
    trySet(out, 'expPi', () => M.exp(M.PI));
    trySet(out, 'expm1Tiny', () => M.expm1(1e-16));
    trySet(out, 'polyExpm1One', () => polyExpm1(1));

    // --- precision / IEEE ---
    trySet(out, 'fround1337', () => {
        if (typeof M.fround !== 'function') {
            throw new Error('no fround');
        }
        return M.fround(1.337);
    });
    trySet(out, 'float64Ulp', () => new Float64Array([1.0000000000000001])[0]);
    trySet(out, 'hypot34', () => M.hypot(3, 4));
    trySet(out, 'hypotMulti', () => M.hypot(1, 2, 3, 4, 5, 6));
    trySet(out, 'imulMaxU32', () => {
        if (typeof M.imul !== 'function') {
            throw new Error('no imul');
        }
        return M.imul(0xffffffff, 5);
    });
    trySet(out, 'objectIs0Neg0', () => Object.is(0, -0));

    return out;
}
